<template>
  <div
    class="video py-16 !m-0"
    role="region"
  >
    <div class="video-inner relative">
      <div
        class="flex flex-col items-center xl:flex-row"
        v-if="title || subtitle || description"
      >
        <div class="title w-full xl:w-1/2" v-if="subtitle || title">
          <span
            class="sub-heading text-base 3xl:text-md font-marcellus text-purple"
            v-if="subtitle"
            >{{ subtitle }}</span
          >
          <h2
            class="heading text-2xl lg:text-6xl 3xl:text-10xl font-marcellus text-grey-700 leading-[30px] md:leading-none"
            v-if="title"
          >
            {{ title }}
          </h2>
        </div>
        <div
          class="description mt-4 xl:mt-0 w-full xl:w-1/2"
          v-if="description"
        >
          <p class="text-base text-grey-700">
            {{ description }}
          </p>
        </div>
      </div>
      <div class="content mt-7 lg:mt-10" v-if="videoUrl">
        <div class="video-wrapper w-full max-w-[1920px] mx-auto">
          <div
            class="video-container relative w-full aspect-square md:aspect-[16/7] bg-grey-100 rounded-[15px] md:rounded-[20px] overflow-hidden"
          >
            <!-- Hidden Checkbox for Play/Pause Control -->
            <input
              v-model="isVideoPlaying"
              type="checkbox"
              id="video-toggle"
              class="custom-checkbox hidden peer"
            />

            <!-- YouTube Iframe -->
            <div class="absolute inset-0 hidden peer-checked:block">
              <ClientOnly>
                <iframe
                  v-if="hasYoutubeConsent && isVideoPlaying"
                  class="w-full h-full"
                  :src="formattedVideoUrl"
                  title="YouTube video"
                  frameborder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; autoplay;"
                  allowfullscreen
                  loading="lazy"
                ></iframe>

                <!-- Fallback message -->
                <div
                  v-else
                  class="flex flex-col items-center justify-center w-full h-full bg-black bg-opacity-50 text-white text-center p-4"
                >
                  {{ $t("cookieConsent.noYoutubeConsent") }} <br/>
                  <button class="btn btn-primary btn-sm mt-4" @click="openPreferences">
                    {{ $t("cookieConsent.individualSettingsButton") }}
                  </button>
                </div>
              </ClientOnly>
            </div>
            <div class="absolute inset-0 peer-checked:hidden">
              <SharedImage
                :src="videoPosterImageDesktop?.media?.url"
                :alt="
                  videoPosterImageDesktop?.media?.alt
                    ? videoPosterImageDesktop?.media?.alt
                    : 'my sofa bed'
                "
                :width="videoPosterImageDesktop?.media?.metaData?.width"
                :height="videoPosterImageDesktop?.media?.metaData?.height"
                imgClass="w-full h-full object-cover"
                loading="lazy"
                format="webp"
                :sources="{
                  desktop: videoPosterImageDesktop?.media,
                  tablet: videoPosterImageTablet?.media,
                  mobile: videoPosterImageMobile?.media
                }"
                :sizes="{
                  desktop: '1326x792',
                  tablet: '920x403',
                  mobile: '560x238',
                }"
              />
            </div>
            <!-- Play Button Overlay -->
            <label
              for="video-toggle"
              class="play-button absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300 cursor-pointer peer-checked:hidden z-10"
              :aria-label="$t('video.ariaLabelPlay')"
            >
              <span
                class="play-icon w-0 h-0 border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent border-l-[16px] border-l-purple ml-1"
              ></span>
            </label>
          </div>
        </div>
      </div>
    </div>
    <CookiePreferencesModal ref="cookieModalRef" />
  </div>
</template>

<script setup lang="ts">
import type { CmsElementVideoDescription } from "~/composables/useCustom";
import { useCookieConsentStore } from "~/stores/cookieConsent";

const props = defineProps<{ content: CmsElementVideoDescription }>();

const {
  videoUrl,
  title,
  subtitle,
  description,
  videoPosterImageDesktop,
  videoPosterImageTablet,
  videoPosterImageMobile,
} = props.content.data;

const cookieConsentStore = useCookieConsentStore();

const hasYoutubeConsent = computed(
  () => cookieConsentStore.hasPreferencesConsent
);

const isVideoPlaying = ref(false);

const { formattedUrl: baseVideoUrl } = useFormattedYoutubeUrl(videoUrl, {
  autoplay: false,
  muted: false,
});

const formattedVideoUrl = computed(() => {
  const url = baseVideoUrl.value;
  if (!url || !isVideoPlaying.value) return url;
  return url.replace("autoplay=0", "autoplay=1");
});

onDeactivated(() => {
  isVideoPlaying.value = false;
});

onBeforeUnmount(() => {
  isVideoPlaying.value = false;
});

import CookiePreferencesModal from "~/components/CookiePreferencesModal.vue";
const cookieModalRef = ref();

const openPreferences = () => {
  cookieModalRef.value?.open();
};
</script>
